import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { ChatbotWidget } from "@/components/layout/ChatbotWidget";
import { ScrollToTop } from "@/components/layout/ScrollToTop";
import { CookieConsent } from "@/components/layout/CookieConsent";
import { StickyCTA } from "@/components/layout/StickyCTA";
import { Toaster } from "sonner";
import { LanguageProvider } from "@/context/LanguageContext";
import { AuthProvider } from "@/context/AuthContext";
import { ClientAuthProvider } from "@/context/ClientAuthContext";
import { ContentProvider } from "@/context/ContentContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { ClientProtectedRoute } from "@/components/auth/ClientProtectedRoute";
import { LanguageRouter, HomeRedirect } from "@/components/routing/LanguageRouter";
import { OrganizationSchema, LocalBusinessSchema, WebsiteSchema } from "@/components/seo/SEO";
import { useImageProtection } from "@/hooks/useImageProtection";
import { GoogleAnalytics } from "@/components/analytics/GoogleAnalytics";

import HomePage from "@/pages/HomePage";
import ServicesPage from "@/pages/ServicesPage";
import PortfolioPage from "@/pages/PortfolioPage";
import PricingPage from "@/pages/PricingPage";
import FAQPage from "@/pages/FAQPage";
import FAQAdminPage from "@/pages/FAQAdminPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import BlogAdminPage from "@/pages/BlogAdminPage";
import ContactPage from "@/pages/ContactPage";
import LoginPage from "@/pages/LoginPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import ServiceDescriptionPage from "@/pages/ServiceDescriptionPage";
import ReviewAdminPage from "@/pages/ReviewAdminPage";
import ContentAdminPage from "@/pages/ContentAdminPage";
import CaseStudyPage from "@/pages/CaseStudyPage";

// Demo portal pages
import DemoCleanForce from "@/pages/demos/DemoCleanForce";
import DemoHomeCare from "@/pages/demos/DemoHomeCare";
import DemoTuingeluk from "@/pages/demos/DemoTuingeluk";

// Client Portal pages
import ClientLoginPage from "@/pages/portal/ClientLoginPage";
import ClientDashboard from "@/pages/portal/ClientDashboard";
import ClientProjectDetail from "@/pages/portal/ClientProjectDetail";

// Portal Admin pages
import PortalClientsAdmin from "@/pages/portal/admin/PortalClientsAdmin";
import PortalProjectsAdmin from "@/pages/portal/admin/PortalProjectsAdmin";
import PortalProjectAdmin from "@/pages/portal/admin/PortalProjectAdmin";

// Admin User Management
import AdminUsersPage from "@/pages/admin/AdminUsersPage";

// Layout wrapper for public pages
const PublicLayout = ({ children }) => (
  <>
    <Navbar />
    <div className="flex-grow">
      {children}
    </div>
    <Footer />
    <ChatbotWidget />
    <StickyCTA />
    <CookieConsent />
  </>
);

function App() {
  // Enable image protection across the site
  useImageProtection();
  
  return (
    <HelmetProvider>
      <AuthProvider>
        <ClientAuthProvider>
          <LanguageProvider>
            <ContentProvider>
              <div className="App min-h-screen flex flex-col">
                <BrowserRouter>
                  <GoogleAnalytics />
                  <OrganizationSchema />
                  <LocalBusinessSchema />
                  <WebsiteSchema />
                  <ScrollToTop />
                  <Routes>
                    {/* Admin Login - No navbar/footer */}
                    <Route path="/admin/login" element={<LoginPage />} />
                    
                    {/* Client Portal routes */}
                    <Route path="/portal/login" element={<ClientLoginPage />} />
                    <Route path="/portal/dashboard" element={
                      <ClientProtectedRoute><ClientDashboard /></ClientProtectedRoute>
                    } />
                    <Route path="/portal/projects/:projectId" element={
                      <ClientProtectedRoute><ClientProjectDetail /></ClientProtectedRoute>
                    } />
                    
                    {/* Demo portal pages - standalone without navbar/footer */}
                    <Route path="/demo/clean-force" element={<DemoCleanForce />} />
                    <Route path="/demo/homecare" element={<DemoHomeCare />} />
                    <Route path="/demo/tuingeluk" element={<DemoTuingeluk />} />
                    
                    {/* Protected admin routes (no language prefix needed) */}
                    <Route path="/faq-admin" element={
                      <PublicLayout>
                        <ProtectedRoute><FAQAdminPage /></ProtectedRoute>
                      </PublicLayout>
                    } />
                    <Route path="/blog-admin" element={
                      <PublicLayout>
                        <ProtectedRoute><BlogAdminPage /></ProtectedRoute>
                      </PublicLayout>
                    } />
                    <Route path="/review-admin" element={
                      <PublicLayout>
                        <ProtectedRoute><ReviewAdminPage /></ProtectedRoute>
                      </PublicLayout>
                    } />
                    <Route path="/content-admin" element={
                      <PublicLayout>
                        <ProtectedRoute><ContentAdminPage /></ProtectedRoute>
                      </PublicLayout>
                    } />
                    
                    {/* Portal Admin routes */}
                    <Route path="/portal-admin" element={
                      <PublicLayout>
                        <ProtectedRoute><PortalProjectsAdmin /></ProtectedRoute>
                      </PublicLayout>
                    } />
                    <Route path="/portal-clients-admin" element={
                      <PublicLayout>
                        <ProtectedRoute><PortalClientsAdmin /></ProtectedRoute>
                      </PublicLayout>
                    } />
                    <Route path="/portal-admin/projects/:projectId" element={
                      <PublicLayout>
                        <ProtectedRoute><PortalProjectAdmin /></ProtectedRoute>
                      </PublicLayout>
                    } />
                    
                    {/* Admin User Management (Super Admin only) */}
                    <Route path="/admin-users" element={
                      <PublicLayout>
                        <ProtectedRoute><AdminUsersPage /></ProtectedRoute>
                      </PublicLayout>
                    } />
                    
                    {/* Root redirect to language-prefixed home */}
                    <Route path="/" element={<HomeRedirect />} />
                  
                  {/* Language-prefixed routes */}
                  <Route path="/:lang" element={<LanguageRouter><PublicLayout><HomePage /></PublicLayout></LanguageRouter>} />
                  
                  {/* Dutch routes */}
                  <Route path="/:lang/diensten" element={<LanguageRouter><PublicLayout><ServicesPage /></PublicLayout></LanguageRouter>} />
                  <Route path="/:lang/prijzen" element={<LanguageRouter><PublicLayout><PricingPage /></PublicLayout></LanguageRouter>} />
                  <Route path="/:lang/voorwaarden" element={<LanguageRouter><PublicLayout><TermsPage /></PublicLayout></LanguageRouter>} />
                  
                  {/* English routes */}
                  <Route path="/:lang/services" element={<LanguageRouter><PublicLayout><ServicesPage /></PublicLayout></LanguageRouter>} />
                  <Route path="/:lang/pricing" element={<LanguageRouter><PublicLayout><PricingPage /></PublicLayout></LanguageRouter>} />
                  <Route path="/:lang/terms" element={<LanguageRouter><PublicLayout><TermsPage /></PublicLayout></LanguageRouter>} />
                  
                  {/* French routes */}
                  <Route path="/:lang/tarifs" element={<LanguageRouter><PublicLayout><PricingPage /></PublicLayout></LanguageRouter>} />
                  <Route path="/:lang/conditions" element={<LanguageRouter><PublicLayout><TermsPage /></PublicLayout></LanguageRouter>} />
                  <Route path="/:lang/confidentialite" element={<LanguageRouter><PublicLayout><PrivacyPage /></PublicLayout></LanguageRouter>} />
                  
                  {/* German routes */}
                  <Route path="/:lang/leistungen" element={<LanguageRouter><PublicLayout><ServicesPage /></PublicLayout></LanguageRouter>} />
                  <Route path="/:lang/preise" element={<LanguageRouter><PublicLayout><PricingPage /></PublicLayout></LanguageRouter>} />
                  <Route path="/:lang/kontakt" element={<LanguageRouter><PublicLayout><ContactPage /></PublicLayout></LanguageRouter>} />
                  <Route path="/:lang/datenschutz" element={<LanguageRouter><PublicLayout><PrivacyPage /></PublicLayout></LanguageRouter>} />
                  <Route path="/:lang/agb" element={<LanguageRouter><PublicLayout><TermsPage /></PublicLayout></LanguageRouter>} />
                  
                  {/* Shared routes (same path in all languages) */}
                  <Route path="/:lang/portfolio" element={<LanguageRouter><PublicLayout><PortfolioPage /></PublicLayout></LanguageRouter>} />
                  <Route path="/:lang/portfolio/:slug" element={<LanguageRouter><PublicLayout><CaseStudyPage /></PublicLayout></LanguageRouter>} />
                  <Route path="/:lang/faq" element={<LanguageRouter><PublicLayout><FAQPage /></PublicLayout></LanguageRouter>} />
                  <Route path="/:lang/blog" element={<LanguageRouter><PublicLayout><BlogPage /></PublicLayout></LanguageRouter>} />
                  <Route path="/:lang/blog/:slug" element={<LanguageRouter><PublicLayout><BlogPostPage /></PublicLayout></LanguageRouter>} />
                  <Route path="/:lang/contact" element={<LanguageRouter><PublicLayout><ContactPage /></PublicLayout></LanguageRouter>} />
                  <Route path="/:lang/privacy" element={<LanguageRouter><PublicLayout><PrivacyPage /></PublicLayout></LanguageRouter>} />
                  
                  {/* Service Description routes */}
                  <Route path="/:lang/dienstbeschrijving" element={<LanguageRouter><PublicLayout><ServiceDescriptionPage /></PublicLayout></LanguageRouter>} />
                  <Route path="/:lang/service-description" element={<LanguageRouter><PublicLayout><ServiceDescriptionPage /></PublicLayout></LanguageRouter>} />
                  <Route path="/:lang/description-service" element={<LanguageRouter><PublicLayout><ServiceDescriptionPage /></PublicLayout></LanguageRouter>} />
                  <Route path="/:lang/dienstbeschreibung" element={<LanguageRouter><PublicLayout><ServiceDescriptionPage /></PublicLayout></LanguageRouter>} />
                  
                  {/* Legacy redirects (old routes without language prefix) */}
                  <Route path="/diensten" element={<Navigate to="/nl/diensten" replace />} />
                  <Route path="/portfolio" element={<Navigate to="/nl/portfolio" replace />} />
                  <Route path="/prijzen" element={<Navigate to="/nl/prijzen" replace />} />
                  <Route path="/faq" element={<Navigate to="/nl/faq" replace />} />
                  <Route path="/blog" element={<Navigate to="/nl/blog" replace />} />
                  <Route path="/blog/:slug" element={<Navigate to="/nl/blog/:slug" replace />} />
                  <Route path="/contact" element={<Navigate to="/nl/contact" replace />} />
                  <Route path="/privacy" element={<Navigate to="/nl/privacy" replace />} />
                  <Route path="/voorwaarden" element={<Navigate to="/nl/voorwaarden" replace />} />
                  
                  {/* Catch-all: redirect unknown routes to home */}
                  <Route path="*" element={<Navigate to="/nl" replace />} />
                </Routes>
                <Toaster position="top-right" richColors />
              </BrowserRouter>
            </div>
          </ContentProvider>
        </LanguageProvider>
      </ClientAuthProvider>
    </AuthProvider>
  </HelmetProvider>
  );
}

export default App;
